/**
 * Client service for GitHub repo dependency graph (V3).
 */

import { supabase, getCurrentUserId, isSupabaseConfigured } from '@/lib/supabase';

export interface GraphExpansionRow {
  from_file: string;
  to_file: string;
  edge_type: string;
  depth: number;
}

export async function expandRepoGraph(
  owner: string,
  repo: string,
  seedFiles: string[],
  options: { maxDepth?: number; maxEdges?: number } = {}
): Promise<GraphExpansionRow[]> {
  const userId = await getCurrentUserId();
  if (!userId || !isSupabaseConfigured || seedFiles.length === 0) return [];

  const { data, error } = await supabase.rpc('expand_github_repo_graph', {
    filter_user_id: userId,
    filter_owner: owner,
    filter_repo: repo,
    seed_files: seedFiles,
    max_depth: options.maxDepth ?? 1,
    max_edges: options.maxEdges ?? 30,
  });

  if (error) {
    console.warn('[RepoGraphService] expand_github_repo_graph failed:', error.message);
    return [];
  }

  return (data ?? []) as GraphExpansionRow[];
}

export async function clearRepoGraphForUser(
  owner: string,
  repo: string
): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId || !isSupabaseConfigured) return;
  await supabase
    .from('github_repo_graph_edges')
    .delete()
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo);
}

export async function clearRepoSymbolsForUser(
  owner: string,
  repo: string
): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId || !isSupabaseConfigured) return;
  await supabase
    .from('github_repo_symbols')
    .delete()
    .eq('user_id', userId)
    .eq('owner', owner)
    .eq('repo', repo);
}

/** Collect neighbor file paths from graph expansion + reverse import lookup. */
export async function getGraphNeighborPaths(
  owner: string,
  repo: string,
  seedFiles: string[],
  repoTreePaths: string[]
): Promise<string[]> {
  const expanded = await expandRepoGraph(owner, repo, seedFiles, { maxDepth: 1, maxEdges: 20 });
  const neighbors = new Set<string>();

  const treeSet = repoTreePaths.length > 0 ? new Set(repoTreePaths) : null;

  for (const row of expanded) {
    if (!row.to_file || row.to_file === row.from_file) continue;
    if (treeSet && !treeSet.has(row.to_file)) continue;
    neighbors.add(row.to_file);
  }

  const userId = await getCurrentUserId();
  if (userId && isSupabaseConfigured) {
    const { data: reverse } = await supabase
      .from('github_repo_graph_edges')
      .select('from_file')
      .eq('user_id', userId)
      .eq('owner', owner)
      .eq('repo', repo)
      .in('to_file', seedFiles.slice(0, 10));

    for (const row of reverse ?? []) {
      const fromFile = row.from_file as string;
      if (!fromFile) continue;
      if (treeSet && !treeSet.has(fromFile)) continue;
      neighbors.add(fromFile);
    }
  }

  for (const seed of seedFiles) {
    const base = seed.replace(/\.[^.]+$/, '');
    for (const p of repoTreePaths) {
      if (p.includes('.test.') || p.includes('.spec.')) {
        const bn = p.split('/').pop()?.replace(/\.(test|spec)\.[^.]+$/, '') ?? '';
        const seedBn = seed.split('/').pop()?.replace(/\.[^.]+$/, '') ?? '';
        if (bn && seedBn && bn === seedBn) neighbors.add(p);
      }
    }
    if (base) {
      for (const suffix of ['.test.ts', '.test.tsx', '.spec.ts', '.spec.tsx']) {
        const candidate = `${base}${suffix}`;
        if (repoTreePaths.includes(candidate)) neighbors.add(candidate);
      }
    }
  }

  for (const s of seedFiles) neighbors.delete(s);
  return [...neighbors];
}
